import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import PageHeader from './PageHeader';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import './Common.css';
import './CulturalInsights.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';

const CulturalInsights = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language: initLang = 'de', proficiencyLevel: initLevel = 'beginner-a1' } = location.state || {};
  const [language, setLanguage] = useState(initLang);
  const [proficiencyLevel, setProficiencyLevel] = useState(initLevel);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('Germany');
  const [countries, setCountries] = useState([]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countryApplied, setCountryApplied] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Explore All');
  const [countrySearch, setCountrySearch] = useState('');
  const [pendingCountry, setPendingCountry] = useState('Germany');
  const [prompts, setPrompts] = useState([]);
  const [showAllPrompts, setShowAllPrompts] = useState(false);
  const [showCollapseTooltip, setShowCollapseTooltip] = useState(false);
  const [showCategoriesPanel, setShowCategoriesPanel] = useState(false);
  const countryRef = useRef(null);

  usePageTimeTracker('cultural-insights', user);

  const breadcrumbs = [
    { label: 'Home', path: '/', current: false },
    { label: 'Explore Learning', path: '/explore-learning', current: false },
    { label: 'Cultural Insights', current: true }
  ];

  const categories = [
    { name: 'Communication Style', icon: '/images/communicationCI.svg', purpleIcon: '/images/comm.svg', question: 'How important is active listening in conversations?' },
    { name: 'Civic Behaviour', icon: '/images/civicCI.svg', purpleIcon: '/images/Civic.svg', question: 'What public behaviors are considered respectful?' },
    { name: 'Workplace Culture', icon: '/images/workplaceCI.svg', purpleIcon: '/images/work.svg', question: 'What are the expectations around punctuality?' },
    { name: 'Social Etiquette', icon: '/images/socialci.svg', purpleIcon: '/images/social.svg', question: 'What are some important social etiquette rules to know?' },
    { name: 'Traditions and Celebrations', icon: '/images/traditionalCI.svg', purpleIcon: '/images/tradition.svg', question: 'What are the major festivals and celebrations?' }
  ];

  // Fetch countries from DB
  useEffect(() => {
    axios.get(`${API_BASE_URL}/cultural-insights/countries`, { withCredentials: true })
      .then(res => {
        if (res.data.success) setCountries(res.data.countries);
      })
      .catch(err => {
        console.error('Failed to fetch countries:', err);
        // Fallback
        setCountries([
          { country_id: 1, country_name: 'Germany', country_code: 'de', flag_url: '/images/germany_flag.png', language_available: 1 },
          { country_id: 2, country_name: 'France', country_code: 'fr', flag_url: '/images/france_flag.png', language_available: 1 },
          { country_id: 3, country_name: 'Spain', country_code: 'es', flag_url: '/images/spain_flag.png', language_available: 1 },
          { country_id: 4, country_name: 'Italy', country_code: 'it', flag_url: '/images/italy_flag.png', language_available: 1 },
          { country_id: 5, country_name: 'Japan', country_code: 'ja', flag_url: '/images/japan_flag.png', language_available: 1 }
        ]);
      });
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showCountryDropdown) return;
    const handleClick = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) setShowCountryDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCountryDropdown]);

  // Fetch prompts from DB
  useEffect(() => {
    if (!countryApplied) return;
    const categoryMap = { 'Communication Style': 1, 'Civic Behaviour': 5, 'Workplace Culture': 2, 'Social Etiquette': 3, 'Traditions and Celebrations': 4 };
    const catId = selectedCategory !== 'Explore All' ? categoryMap[selectedCategory] : null;
    const url = catId ? `${API_BASE_URL}/cultural-insights/prompts?category_id=${catId}` : `${API_BASE_URL}/cultural-insights/prompts`;
    axios.get(url, { withCredentials: true })
      .then(res => {
        if (res.data.success) setPrompts(res.data.prompts);
      })
      .catch(err => console.error('Failed to fetch prompts:', err));
  }, [countryApplied, selectedCategory]);

  const handleCategoryClick = (cat) => {
    navigate('/cultural-insights-chat', { state: { language, country: selectedCountry, category: cat, flagUrl: getCountryFlag(selectedCountry) } });
  };

  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      'Communication Style': '/images/communicationCI.svg',
      'Civic Behavior': '/images/civicCI.svg',
      'Civic Behaviour': '/images/civicCI.svg',
      'Workplace Culture': '/images/workplaceCI.svg',
      'Social Etiquette': '/images/socialci.svg',
      'Traditions and Celebrations': '/images/traditionalCI.svg'
    };
    return iconMap[categoryName] || '/images/communicationCI.svg';
  };

  const handleApplyCountry = () => {
    setSelectedCountry(pendingCountry);
    setShowCountryDropdown(false);
    // Update language based on selected country's code
    const country = countries.find(c => c.country_name === pendingCountry);
    if (country?.country_code) setLanguage(country.country_code);
  };

  const getCountryFlag = (name) => {
    const country = countries.find(c => c.country_name === name);
    return country?.flag_url || '/images/flag-en.svg';
  };

  const getLanguageName = () => {
    const map = { 'de': 'German', 'en': 'English', 'fr': 'French', 'es': 'Spanish', 'it': 'Italian', 'ja': 'Japanese', 'ca': 'English', 'ch': 'German', 'th': 'Thai', 'no': 'Norwegian', 'ae': 'Arabic', 'in': 'Hindi' };
    return map[language] || 'German';
  };

  const filteredCountries = countries.filter(c =>
    c.country_name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  return (
    <div className={`ci-page${showCategoriesPanel ? ' panel-open' : ''}`}>
      <Header user={user} />
      <div className="nav-header-container">
        <SideNav />
        <PageHeader breadcrumbs={breadcrumbs} language={language} proficiencyLevel={proficiencyLevel} onLanguageChange={setLanguage} onLevelChange={setProficiencyLevel}
          rightContent={<></>}
        />
      </div>

      <main className="ci-content">
        <div className="ci-hero">
          <h1 className="ci-title"><span className="ci-bold">Navigate</span> <span className="ci-italic">Global</span> <span className="ci-bold">Cultures</span></h1>
          <p className="ci-subtitle">Understanding a language is deeper when you know its culture. Ask me anything about customs, traditions, and social norms from countries around the world.</p>
        </div>

        <div className="ci-country-bar" ref={countryRef}>
          {countryApplied ? (
            <>
              <span className="ci-country-text"><strong>{selectedCountry}</strong> &nbsp;|</span>
              <span className="ci-change-country" onClick={() => { setShowCountryDropdown(!showCountryDropdown); setPendingCountry(selectedCountry); setCountrySearch(''); }}>
                Change Country <img src="/images/purplearrow.svg" alt="" width="16" height="16" />
              </span>
            </>
          ) : (
            <span className="ci-country-text ci-choose-country" onClick={() => { setShowCountryDropdown(!showCountryDropdown); setPendingCountry(''); setCountrySearch(''); }}>
              Choose a country to explore <img src="/images/purplearrow.svg" alt="" width="16" height="16" />
            </span>
          )}

          {showCountryDropdown && (
            <div className="ci-country-dropdown">
              <div className="ci-country-search">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="#808080" strokeWidth="2" strokeLinecap="round"/></svg>
                <input type="text" placeholder="Search" value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} />
              </div>
              <div className="ci-country-header">Countries</div>
              <div className="ci-country-list">
                {filteredCountries.length === 0 && (
                  <div className="ci-country-empty">No countries found</div>
                )}
                {filteredCountries.map(c => (
                  <div key={c.country_id} className={`ci-country-item${pendingCountry === c.country_name ? ' selected' : ''}`} onClick={() => { 
                    setPendingCountry(c.country_name);
                    setSelectedCountry(c.country_name);
                    setShowCountryDropdown(false);
                    if (c.country_code) setLanguage(c.country_code);
                    // Check if disclaimer already accepted today for this country
                    axios.get(`${API_BASE_URL}/cultural-insights/disclaimer/check?country=${encodeURIComponent(c.country_name)}&_t=${Date.now()}`, { withCredentials: true })
                      .then(res => {
                        if (res.data.success && res.data.accepted) {
                          setCountryApplied(true);
                        } else {
                          setShowDisclaimer(true);
                          setDisclaimerChecked(false);
                        }
                      })
                      .catch(() => {
                        setShowDisclaimer(true);
                        setDisclaimerChecked(false);
                      });
                  }}>
                    <img src={c.flag_url || getCountryFlag(c.country_name)} alt="" width="20" height="14" />
                    <span>{c.country_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {countryApplied && (
          <>
            <div className="ci-section-header">
              <div className="ci-section-title">Explore with Categories</div>
            </div>
            <a className="ci-explore-all" onClick={() => navigate('/cultural-insights-chat', { state: { language, country: selectedCountry, flagUrl: getCountryFlag(selectedCountry) } })}>Explore All</a>
            <div className="ci-category-cards">
              {categories.map((cat, i) => (
                <button key={i} className="ci-category-card" onClick={() => handleCategoryClick(cat.name)}>
                  <span className="ci-category-name">{cat.name}</span>
                  <img src={cat.icon} alt="" className="ci-category-icon" />
                </button>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Collapse Icon with Tooltip and Categories Panel */}
      <div className="ci-collapse-wrapper ci-landing-collapse">
        <button className="ci-collapse-icon" onMouseEnter={() => { if (!showCategoriesPanel) setShowCollapseTooltip(true); }} onMouseLeave={() => setShowCollapseTooltip(false)} onClick={() => { if (!countryApplied) return; setShowCategoriesPanel(!showCategoriesPanel); setShowCollapseTooltip(false); }} style={{ opacity: countryApplied ? 1 : 0.4, cursor: countryApplied ? 'pointer' : 'default' }}>
          <img src="/images/CollapseIcon.svg" alt="" />
        </button>
        {showCollapseTooltip && !showCategoriesPanel && (
          <div className="ci-collapse-label">
            <img src="/images/TooltipBubble.svg" alt="" className="ci-collapse-bubble" />
            <span className="ci-collapse-text">Explore<br/>Categories</span>
          </div>
        )}
        {showCategoriesPanel && (
          <div className="ci-landing-categories-panel">
            <div className="ci-landing-panel-header" onClick={() => setShowCategoriesPanel(false)}>
              <span>Category</span>
              <img src="/images/purplearrow.svg" alt="" width="12" height="12" />
            </div>
            {categories.map((cat, i) => (
              <div key={i} className="ci-landing-panel-item" onClick={() => { handleCategoryClick(cat.name); setShowCategoriesPanel(false); }}>
                <img src={cat.purpleIcon} alt="" width="20" height="20" />
                <span>{cat.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />

      {showDisclaimer && (
        <div className="ci-overlay">
          <div className="ci-modal">
            <div className="ci-modal-header">
              <div className="ci-modal-accent"></div>
              <span>Disclaimer</span>
            </div>
            <div className="ci-modal-body">
              <p>These cultural insights are intended to provide general guidance based on commonly observed cultural tendencies and majority perspectives. They are not meant to stereotype individuals, define all members of a culture, or suggest that one culture is better than another. Individual experiences, behaviors, and perspectives may vary, and users are encouraged to approach the content with openness and cultural sensitivity.</p>
            </div>
            <div className="ci-modal-footer">
              <label className="ci-check-label">
                <input type="checkbox" checked={disclaimerChecked} onChange={(e) => setDisclaimerChecked(e.target.checked)} />
                <span>I Understand</span>
              </label>
              <button className="ci-continue-btn" disabled={!disclaimerChecked} onClick={() => { 
                setShowDisclaimer(false); 
                setCountryApplied(true);
                axios.post(`${API_BASE_URL}/cultural-insights/disclaimer/accept`, { country: selectedCountry }, { withCredentials: true }).catch(() => {});
              }}>Continue</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CulturalInsights;
